import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { UseMutationOptions, useQueryClient } from 'react-query';
import http from '../config/http-service';
import type { MasterProductFamily, UpdateMasterProductFamilyPayload } from './types';
import { masterProductFamiliesEndpoint } from './use-get-master-product-families-api';

interface Params {
  id: number;
  data: UpdateMasterProductFamilyPayload;
}

type TData = MasterProductFamily;
type TError = AxiosError;
type TVariables = Params;

type Options = Omit<UseMutationOptions<TData, TError, TVariables>, 'mutationFn'>;
type TReturnType = ReturnType<typeof useMutationWrapper<TData, TError, TVariables>>;

// PUT api/v1/cms/master-product-families/:id — response body is { data: MasterProductFamily }.
export function useUpdateMasterProductFamilyApi(options?: Options): TReturnType {
  const queryClient = useQueryClient();
  const { onSuccess: userOnSuccess, ...restOptions } = options || {};

  const mutationFn = async ({ id, data }: TVariables): Promise<TData> => {
    const response = await http.put(`${masterProductFamiliesEndpoint}/${id}`, data);
    return response.data.data;
  };

  return useMutationWrapper(mutationFn, {
    mutationKey: [`${masterProductFamiliesEndpoint}/update`],
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries([masterProductFamiliesEndpoint]);
      userOnSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
}
